import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangleIcon, ClockIcon, ChevronRightIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getTenant } from '../../../theme/tokens';
import { useCompany } from '../../../app/providers/CompanyProvider';
interface AttentionItem {
  id: string;
  type: 'dispute' | 'refund';
  title: string;
  description: string;
  dueDate?: string;
  amount?: string;
  urgent?: boolean;
  link: string;
  companyId: string;
}
export function AttentionNeededWidget() {
  const {
    t
  } = useTranslation('analytics');
  const navigate = useNavigate();
  const {
    selectedCompany
  } = useCompany();
  // All attention items with company assignments
  const allAttentionItems: AttentionItem[] = [{
    id: '1',
    type: 'dispute',
    title: 'Dispute requires response',
    description: 'Dispute #DP-2024-0001',
    dueDate: 'Due in 2 hours',
    amount: `${getTenant().currency.code} 100.01`,
    urgent: true,
    link: '/disputes/1',
    companyId: 'submerchant-kl-001'
  }, {
    id: '2',
    type: 'dispute',
    title: 'Dispute requires response',
    description: 'Dispute #DP-2024-0091',
    dueDate: 'Due in 5 hours',
    amount: `${getTenant().currency.code} 1,538.94`,
    urgent: true,
    link: '/disputes/91',
    companyId: 'submerchant-kl-002'
  }, {
    id: '3',
    type: 'refund',
    title: 'Refund pending review',
    description: 'Refund #RF-2024-0045',
    dueDate: 'Due in 1 day',
    amount: `${getTenant().currency.code} 45,000.00`,
    urgent: false,
    link: '/refund-detail/RF-2024-0045',
    companyId: 'submerchant-kl-003'
  }];
  // Filter items based on selected company
  const getItemsForCompany = () => {
    if (selectedCompany.type === 'master') {
      // Master merchant: show all items
      return allAttentionItems;
    } else {
      // Specific sub-merchant: filter by companyId
      return allAttentionItems.filter(item => item.companyId === selectedCompany.id);
    }
  };
  const allItems = getItemsForCompany();
  const attentionItems = allItems.slice(0, 3);
  const getTypeColor = (type: string) => {
    switch (type) {
      case 'dispute':
        return 'text-red-700 bg-red-50 border border-red-100';
      case 'refund':
        return 'text-[#9F140F] bg-[#FEE7E5] border border-[#FBD4D1]';
      default:
        return 'text-gray-700 bg-gray-50 border border-gray-100';
    }
  };
  const urgentCount = attentionItems.filter(item => item.urgent).length;
  return <div className="bg-white rounded-lg border border-gray-200 overflow-hidden flex flex-col h-full" data-id="element-1312">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between bg-white" data-id="element-1313">
        <h3 className="text-sm font-semibold text-gray-900 flex items-center" data-id="element-1314">
          <AlertTriangleIcon size={16} className="mr-2 text-[#9F140F]" aria-hidden="true" data-id="element-1315" />
          {t('components.AttentionNeededWidget.attention_needed')}
        </h3>
        {urgentCount > 0 && <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-100" data-id="element-1316">
            {urgentCount} {t('components.AttentionNeededWidget.urgent')}
          </span>}
      </div>

      {/* Body */}
      <div className="bg-white flex-1 flex flex-col" data-id="element-1317">
        {attentionItems.length > 0 ? <div className="divide-y divide-gray-100 flex-1" data-id="element-1318">
            {attentionItems.map(item => <button key={item.id} onClick={() => navigate(item.link)} className="w-full text-left px-4 py-2.5 hover:bg-gray-50 transition-colors group grid grid-cols-[auto_auto_minmax(0,1fr)_auto_auto_auto] items-center gap-x-2.5" data-id="element-1319">
                {/* Type badge */}
                <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium uppercase tracking-wider ${getTypeColor(item.type)}`} data-id="element-1320">
                  {item.type}
                </span>

                {/* Urgent badge (reserves column even when absent so rows align) */}
                <span className="min-w-[56px]" data-id="element-1321">
                  {item.urgent && <span className="inline-flex items-center text-[10px] text-red-700 font-semibold" data-id="element-1322">
                      <ClockIcon size={10} className="mr-0.5" aria-hidden="true" data-id="element-1323" />
                      {t('components.AttentionNeededWidget.urgent_2')}
                    </span>}
                </span>

                {/* Description (flexes, truncates) */}
                <span className="text-sm font-medium text-gray-900 truncate min-w-0" data-id="element-1324">
                  {item.description}
                </span>

                {/* Due date (right-aligned column) */}
                <span className="text-xs text-gray-600 whitespace-nowrap text-right" data-id="element-1325">
                  {item.dueDate ?? ''}
                </span>

                {/* Amount (right-aligned, tabular for vertical alignment) */}
                <span className="text-xs font-semibold text-gray-900 whitespace-nowrap text-right tabular-nums min-w-[88px]" data-id="element-1326">
                  {item.amount ?? ''}
                </span>

                <ChevronRightIcon size={14} className="text-gray-400 group-hover:text-blue-600 transition-colors" aria-hidden="true" data-id="element-1327" />
              </button>)}
          </div> : <div className="flex-1 flex items-center justify-center py-8" data-id="element-1328">
            <p className="text-sm text-gray-500" data-id="element-1329">No items require attention</p>
          </div>}
      </div>

      {/* Footer */}
      <div className="bg-[#FEE7E5] px-4 py-3 rounded-b-lg flex items-center justify-between h-12" data-id="element-1330">
        <div className="flex items-center gap-1.5 min-w-0" data-id="element-1331">
          <AlertTriangleIcon size={14} className="text-[#9F140F] flex-shrink-0" aria-hidden="true" data-id="element-1332" />

          <span className="text-xs font-medium text-[#5C0C08] truncate" data-id="element-1333">
            {allItems.length}{' '}
            {t('components.AttentionNeededWidget.items_require_your_attention')}
          </span>
        </div>
        <button onClick={() => navigate('/disputes')} className="text-xs text-[#9F140F] hover:text-[#5C0C08] flex items-center flex-shrink-0 ml-3" data-id="element-1334">
          {t('components.AttentionNeededWidget.view_all_items')}
          <ChevronRightIcon size={14} className="ml-1 inline" data-id="element-1335" />
        </button>
      </div>
    </div>;
}